import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  RouterProvider,
  redirect,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import LeftNavSideBar from "@/components/common/LeftNavSideBar";
import {
  Sidebar,
  SidebarInset,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { useFeedLastLocationSync } from "@/hooks/useFeedLastLocationSync";
import { useLastVisitedLocationSync } from "@/hooks/useLastVisitedLocationSync";
import initializeLogging from "@/lib/analytics/initializeLogging";
import {
  getAuthenticatedLandingHref,
  resolveCatchAllRedirect,
  resolveSsoReturnLoginRedirect,
} from "@/lib/auth/authLoginUtils";
import { forgotPasswordConfirmSearchSchema } from "@/lib/auth/authValidation";
import { resolveAvailableFeedId } from "@/lib/auth/feedLastLocationUtils";
import {
  readSsoReturnParams,
  SSO_CODE_PARAM,
  SSO_ERROR_PARAM,
} from "@/lib/auth/ssoReturnUtils";
import { parseBriefingRouteSearch } from "@/lib/briefing/briefingUtils";
import { gb } from "@/lib/feature-management";
import AnalysisRoute from "@/routes/analysis/AnalysisRoute";
import ForgotPasswordConfirmRoute from "@/routes/auth/ForgotPasswordConfirmRoute";
import ForgotPasswordRoute from "@/routes/auth/ForgotPasswordRoute";
import LoginRoute from "@/routes/auth/LoginRoute";
import RegisterRoute from "@/routes/auth/RegisterRoute";
import ChatRoute from "@/routes/chat/ChatRoute";
import ArticlesTab from "@/routes/discovery/ArticlesTab";
import BroadcastClipsTab from "@/routes/discovery/BroadcastClipsTab";
import DiscoveryRoute from "@/routes/discovery/DiscoveryRoute";
import NarrativeClustersTab from "@/routes/discovery/NarrativeClustersTab";
import PodcastEpisodesTab from "@/routes/discovery/PodcastEpisodesTab";
import NewsAlertsRoute from "@/routes/news-alerts/NewsAlertsRoute";
import BriefingsIndexRoute from "@/routes/scheduled-briefings/BriefingsIndexRoute";
import BriefingCreateRoute from "@/routes/scheduled-briefings/create/BriefingCreateRoute";
import ScheduledBriefingsRoute from "@/routes/scheduled-briefings/ScheduledBriefingsRoute";
import TemplateStudioCreateRoute from "@/routes/scheduled-briefings/template-studio/TemplateStudioCreateRoute";
import TemplatePreviewRoute from "@/routes/scheduled-briefings/templates/TemplatePreviewRoute";
import TemplatesLandingRoute from "@/routes/scheduled-briefings/templates/TemplatesLandingRoute";
import ShareOfVoiceRoute from "@/routes/share-of-voice/ShareOfVoiceRoute";
import { hydrateAuthUser } from "@/store/auth.actions";
import useAuth from "@/store/auth.slice";
import useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";
import { fetchNewsFeeds } from "@/store/news-feeds/news-feeds.async-actions";
import useNewsFeedsStore from "@/store/news-feeds/news-feeds.slice";
import {
  tanStackRouteSearchSchema,
  tanStackRouteSearchSchemaAnalysis,
} from "@/types/url-params.types";

// =============================================================================
// ROOT LAYOUT
// =============================================================================

const RootLayout = () => {
  return (
    <>
      <Outlet />
      {import.meta.env.DEV && (
        <TanStackRouterDevtools position="bottom-right" />
      )}
    </>
  );
};

const rootRoute = createRootRoute({
  component: RootLayout,
});

// =============================================================================
// AUTHENTICATED LAYOUT (with loader)
// =============================================================================

const AuthenticatedLayout = () => {
  useLastVisitedLocationSync();
  useFeedLastLocationSync();

  return (
    <SidebarProvider className="h-screen" defaultOpen={false}>
      <div className="**:data-[sidebar=sidebar]:bg-[#F9F9F9]!">
        <Sidebar
          collapsible="icon"
          forceDesktopLayout
          className="border-r border-gray-100"
        >
          <LeftNavSideBar />
        </Sidebar>
      </div>

      <SidebarInset className="overflow-auto">
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
};

/**
 * Waits for Zustand persist middleware to finish restoring auth state from
 * localStorage. In production builds the router can initialise before the
 * async hydration microtask completes, causing a false "not logged in" check.
 */
const waitForAuthHydration = async () => {
  if (useAuth.persist.hasHydrated()) return;
  await new Promise<void>((resolve) => {
    const unsub = useAuth.persist.onFinishHydration(() => {
      unsub();
      resolve();
    });
    // Re-check: hydration may have completed between the outer check and
    // registering the callback above.
    if (useAuth.persist.hasHydrated()) {
      unsub();
      resolve();
    }
  });
};

const authenticatedRoute = createRoute({
  id: "_authenticated",
  getParentRoute: () => rootRoute,
  component: AuthenticatedLayout,
  beforeLoad: async () => {
    await waitForAuthHydration();
    if (!useAuth.getState().loggedIn) {
      throw redirect({ to: "/auth/login" });
    }
    const allowed = await hydrateAuthUser();
    if (!allowed) {
      throw redirect({ to: "/auth/login" });
    }
  },
  loader: async () => {
    // Fetch shared data for all child routes
    await Promise.all([
      fetchNewsFeeds(),
      useFilterDictionary.getState().fetchSidebarFilters(),
    ]);
    return {};
  },
});

/** Redirects to stored last-visited location, or default landing (`/chat/new`). */
const throwRedirectToAuthenticatedLanding = async () => {
  throw redirect({
    href: await getAuthenticatedLandingHref(),
  });
};

// =============================================================================
// AUTH ROUTES
// =============================================================================

/** Both params are optional so existing links to `/auth/login` stay search-free. */
interface SsoReturnSearch {
  [SSO_CODE_PARAM]?: string;
  [SSO_ERROR_PARAM]?: string;
}

const readSsoReturnParam = (value: unknown) =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const authLoginRoute = createRoute({
  path: "/auth/login",
  getParentRoute: () => rootRoute,
  component: LoginRoute,
  validateSearch: (search: Record<string, unknown>): SsoReturnSearch => ({
    [SSO_CODE_PARAM]: readSsoReturnParam(search[SSO_CODE_PARAM]),
    [SSO_ERROR_PARAM]: readSsoReturnParam(search[SSO_ERROR_PARAM]),
  }),
  beforeLoad: async ({ location }) => {
    await waitForAuthHydration();
    // An SSO return owns the page: a stale session must not swallow the code.
    const ssoReturn = readSsoReturnParams(location.searchStr, location.hash);
    if (ssoReturn.code || ssoReturn.error) {
      return;
    }
    if (useAuth.getState().loggedIn) {
      await throwRedirectToAuthenticatedLanding();
    }
  },
});

const authRegisterRoute = createRoute({
  path: "/auth/register",
  getParentRoute: () => rootRoute,
  component: RegisterRoute,
  beforeLoad: async () => {
    await waitForAuthHydration();
    if (useAuth.getState().loggedIn) {
      await throwRedirectToAuthenticatedLanding();
    }
  },
});

const authForgotPasswordRoute = createRoute({
  path: "/auth/forgot-password",
  getParentRoute: () => rootRoute,
  component: ForgotPasswordRoute,
});

const authForgotPasswordConfirmRoute = createRoute({
  path: "/auth/forgot-password-confirm",
  getParentRoute: () => rootRoute,
  validateSearch: (search) => {
    const parsed = forgotPasswordConfirmSearchSchema.safeParse(search);
    if (!parsed.success) {
      throw redirect({ to: "/auth/forgot-password" });
    }
    return parsed.data;
  },
  component: ForgotPasswordConfirmRoute,
});

// =============================================================================
// CATCH-ALL ROUTE
// =============================================================================

/** Unmatched paths always land on a guaranteed-valid route (never stored location). */
const catchAllRoute = createRoute({
  path: "$",
  getParentRoute: () => rootRoute,
  beforeLoad: async ({ location }) => {
    await waitForAuthHydration();

    // The SSO callback returns to the app root, which is an unmatched path, so
    // the return params have to survive this redirect to reach the login route.
    const ssoReturn = resolveSsoReturnLoginRedirect(
      location.searchStr,
      location.hash,
    );
    if (ssoReturn) {
      throw redirect(ssoReturn);
    }

    throw redirect(resolveCatchAllRedirect(useAuth.getState().loggedIn));
  },
});

// =============================================================================
// DISCOVERY ROUTES
// =============================================================================

/**
 * /discovery - redirects to the currently selected feed, or first feed on initial load
 */
const discoveryIndexRoute = createRoute({
  path: "/discovery",
  getParentRoute: () => authenticatedRoute,
  loader: async () => {
    // Loaders run in parallel, so we must await feeds here
    // (can't rely on parent loader completing first)
    await fetchNewsFeeds();

    const { allNewsFeeds, currentFeedId } = useNewsFeedsStore.getState();
    const targetFeed =
      allNewsFeeds.find((f) => String(f.id) === String(currentFeedId)) ??
      allNewsFeeds[0];

    if (targetFeed) {
      throw redirect({
        to: "/discovery/$feedId",
        params: { feedId: String(targetFeed.id) },
      });
    }

    return {};
  },
  component: () => (
    <div className="p-8">
      <h1 className="text-2xl font-serif text-gray-800">No feeds available</h1>
      <p className="text-slate-600 mt-2">Create a newsfeed to get started.</p>
    </div>
  ),
});

/**
 * /discovery/:feedId - Discovery layout (header, control panel, outlet for tab content)
 */
const discoveryFeedRoute = createRoute({
  path: "/discovery/$feedId",
  getParentRoute: () => authenticatedRoute,
  validateSearch: tanStackRouteSearchSchema,
  component: DiscoveryRoute,
});

/**
 * /discovery/:feedId/ - index redirects to narrative clusters
 */
const discoveryFeedIndexRoute = createRoute({
  path: "/",
  getParentRoute: () => discoveryFeedRoute,
  loader: ({ params }) => {
    throw redirect({
      to: "/discovery/$feedId/clusters",
      params: { feedId: params.feedId },
    });
  },
});

/**
 * /discovery/:feedId/articles - Articles tab
 */
const discoveryArticlesRoute = createRoute({
  path: "articles",
  getParentRoute: () => discoveryFeedRoute,
  component: ArticlesTab,
});

/**
 * /discovery/:feedId/clusters - Narrative Clusters tab
 */
const discoveryClustersRoute = createRoute({
  path: "clusters",
  getParentRoute: () => discoveryFeedRoute,
  component: NarrativeClustersTab,
});

/**
 * /discovery/:feedId/broadcast - Broadcast Clips tab
 * Gated behind the `enable-broadcast-clips-tab` feature flag; redirects to
 * the clusters tab if the flag is off so direct URL access is also blocked.
 */
const discoveryBroadcastRoute = createRoute({
  path: "broadcast",
  getParentRoute: () => discoveryFeedRoute,
  beforeLoad: ({ params }) => {
    if (!gb.getFeatureValue("enable-broadcast-clips-tab", false)) {
      throw redirect({
        to: "/discovery/$feedId/clusters",
        params: { feedId: params.feedId },
      });
    }
  },
  component: BroadcastClipsTab,
});

/**
 * /discovery/:feedId/podcast - Podcast Episodes tab
 * Gated behind the `enable-podcast-episodes-tab` feature flag; redirects to
 * the clusters tab if the flag is off so direct URL access is also blocked.
 */
const discoveryPodcastRoute = createRoute({
  path: "podcast",
  getParentRoute: () => discoveryFeedRoute,
  beforeLoad: ({ params }) => {
    if (!gb.getFeatureValue("enable-podcast-episodes-tab", false)) {
      throw redirect({
        to: "/discovery/$feedId/clusters",
        params: { feedId: params.feedId },
      });
    }
  },
  component: PodcastEpisodesTab,
});

// =============================================================================
// ANALYSIS ROUTES
// =============================================================================

/**
 * /analysis - redirects to the currently selected feed, or first feed on initial load
 */
const analysisIndexRoute = createRoute({
  path: "/analysis",
  getParentRoute: () => authenticatedRoute,
  loader: async () => {
    await fetchNewsFeeds();

    const { allNewsFeeds, currentFeedId } = useNewsFeedsStore.getState();
    const targetFeed =
      allNewsFeeds.find((f) => String(f.id) === String(currentFeedId)) ??
      allNewsFeeds[0];

    if (targetFeed) {
      throw redirect({
        to: "/analysis/$feedId",
        params: { feedId: String(targetFeed.id) },
      });
    }

    return {};
  },
  component: () => (
    <div className="p-8">
      <h1 className="text-2xl font-serif text-gray-800">No feeds available</h1>
      <p className="text-slate-600 mt-2">Create a newsfeed to get started.</p>
    </div>
  ),
});

/**
 * Uses schema that defaults selected to News category.
 */
const analysisFeedRoute = createRoute({
  path: "/analysis/$feedId",
  getParentRoute: () => authenticatedRoute,
  validateSearch: tanStackRouteSearchSchemaAnalysis,
  loader: async ({ params }) => {
    await fetchNewsFeeds();

    const { allNewsFeeds } = useNewsFeedsStore.getState();
    const targetFeedId = resolveAvailableFeedId(params.feedId, allNewsFeeds);

    if (targetFeedId != null && targetFeedId !== params.feedId) {
      throw redirect({
        to: "/analysis/$feedId",
        params: { feedId: targetFeedId },
      });
    }

    return {};
  },
  component: AnalysisRoute,
});

// =============================================================================
// SHARE OF VOICE ROUTES
// =============================================================================

/**
 * /share-of-voice - Dynamic Share of Voice page
 * Same search schema as Discovery (filter/date URL params; no default category).
 */
const shareOfVoiceRoute = createRoute({
  path: "/share-of-voice",
  getParentRoute: () => authenticatedRoute,
  validateSearch: tanStackRouteSearchSchema,
  component: ShareOfVoiceRoute,
});

// =============================================================================
// NEWS ALERTS ROUTES
// =============================================================================

/**
 * /news-alerts - News Alerts page
 * Simple page with no URL params (alerts are managed via store/modals)
 */
const newsAlertsRoute = createRoute({
  path: "/news-alerts",
  getParentRoute: () => authenticatedRoute,
  component: NewsAlertsRoute,
});

// =============================================================================
// CHAT ROUTES
// =============================================================================

// Bare /chat redirects into /chat/$conversationId (default segment is "new").
const chatIndexRoute = createRoute({
  path: "/chat",
  getParentRoute: () => authenticatedRoute,
  beforeLoad: () => {
    throw redirect({
      to: "/chat/$conversationId",
      params: { conversationId: "new" },
    });
  },
});

const chatConversationRoute = createRoute({
  path: "/chat/$conversationId",
  getParentRoute: () => authenticatedRoute,
  component: ChatRoute,
});

// =============================================================================
// SCHEDULED BRIEFINGS ROUTES
// =============================================================================

const scheduledBriefingsRoute = createRoute({
  path: "/scheduled-briefings",
  getParentRoute: () => authenticatedRoute,
  // Gates every child route.
  beforeLoad: requireScheduledBriefingsV1,
  component: ScheduledBriefingsRoute,
});

const scheduledBriefingsIndexRoute = createRoute({
  path: "/",
  getParentRoute: () => scheduledBriefingsRoute,
  component: BriefingsIndexRoute,
});

interface BriefingCreateSearch {
  /** Preselects a template so the flow opens on the configure step. */
  templateId?: string;
}

/** Hand-typed numeric ids arrive as numbers from TanStack's JSON search parsing. */
const readTemplateIdParam = (value: unknown) => {
  const templateId = typeof value === "number" ? String(value) : value;
  return typeof templateId === "string" && templateId.trim().length > 0
    ? templateId
    : undefined;
};

const scheduledBriefingCreateRoute = createRoute({
  path: "create",
  getParentRoute: () => scheduledBriefingsRoute,
  validateSearch: (search: Record<string, unknown>): BriefingCreateSearch => ({
    templateId: readTemplateIdParam(search.templateId),
  }),
  component: BriefingCreateRoute,
});

const scheduledBriefingDetailRoute = createRoute({
  path: "$briefingId",
  getParentRoute: () => scheduledBriefingsRoute,
  component: BriefingCreateRoute,
});

async function requireScheduledBriefingsV1() {
  if (!gb.getFeatureValue("enable-scheduled-briefings-v1", false)) {
    await throwRedirectToAuthenticatedLanding();
  }
}

async function requireCanCreateTemplates() {
  if (!gb.getFeatureValue("can-create-templates", false)) {
    await throwRedirectToAuthenticatedLanding();
  }
}

const scheduledBriefingTemplatesRoute = createRoute({
  path: "templates",
  getParentRoute: () => scheduledBriefingsRoute,
  component: TemplatesLandingRoute,
});

interface TemplateStudioCreateSearch {
  /** Seeds Template Studio with a copy of this template; Save creates a new one. */
  duplicateFrom?: string;
}

const scheduledBriefingTemplateCreateRoute = createRoute({
  path: "templates/create",
  getParentRoute: () => scheduledBriefingsRoute,
  beforeLoad: requireCanCreateTemplates,
  validateSearch: (
    search: Record<string, unknown>,
  ): TemplateStudioCreateSearch => ({
    duplicateFrom: readTemplateIdParam(search.duplicateFrom),
  }),
  component: TemplateStudioCreateRoute,
});

interface TemplateDetailSearch {
  /** `details` shows the locked preview even when the owner could edit in Template Studio. */
  view?: "details";
}

const scheduledBriefingTemplateDetailRoute = createRoute({
  path: "templates/$templateId",
  getParentRoute: () => scheduledBriefingsRoute,
  validateSearch: (search: Record<string, unknown>): TemplateDetailSearch => ({
    view: search.view === "details" ? "details" : undefined,
  }),
  component: TemplatePreviewRoute,
});

// =============================================================================
// BRIEFING ROUTES
// =============================================================================

/** Gotenberg-only route; lazy so herald-assistant-pdf.css stays out of the main bundle. */
const briefingRoute = createRoute({
  path: "/briefing/$conversationId",
  getParentRoute: () => rootRoute,
  validateSearch: parseBriefingRouteSearch,
  component: lazyRouteComponent(
    () => import("@/routes/briefing/BriefingRoute"),
  ),
});

// =============================================================================
// ROUTER
// =============================================================================

export const routeTree = rootRoute.addChildren([
  authLoginRoute,
  authRegisterRoute,
  authForgotPasswordRoute,
  authForgotPasswordConfirmRoute,
  briefingRoute,
  authenticatedRoute.addChildren([
    chatIndexRoute,
    chatConversationRoute,
    discoveryIndexRoute,
    discoveryFeedRoute.addChildren([
      discoveryFeedIndexRoute,
      discoveryArticlesRoute,
      discoveryClustersRoute,
      discoveryBroadcastRoute,
      discoveryPodcastRoute,
    ]),
    analysisIndexRoute,
    analysisFeedRoute,
    shareOfVoiceRoute,
    newsAlertsRoute,
    scheduledBriefingsRoute.addChildren([
      scheduledBriefingsIndexRoute,
      scheduledBriefingCreateRoute,
      scheduledBriefingDetailRoute,
      scheduledBriefingTemplatesRoute,
      scheduledBriefingTemplateCreateRoute,
      scheduledBriefingTemplateDetailRoute,
    ]),
  ]),
  catchAllRoute,
]);

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

initializeLogging(router);

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const TanStackRouter = () => {
  return <RouterProvider router={router} />;
};

export default TanStackRouter;
